import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { subscribe, POSITION } from '../../utils/toast';
import './notifications.css';

/* ── Type → icon + accent (colours come from the design tokens in CSS) ── */
const ICONS = {
    success: (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    ),
    error: (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    ),
    warn: (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    info: (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
    ),
};
ICONS.default = ICONS.info;

const DEFAULT_TITLE = {
    success: () => i18next.t('toaster.success', 'Success'),
    error: () => i18next.t('toaster.error', 'Something went wrong'),
    warn: () => i18next.t('toaster.warn', 'Heads up'),
    info: () => i18next.t('toaster.info', 'Notice'),
    default: () => i18next.t('toaster.default', 'Notice'),
};

/* A single toast — owns its own enter/leave animation + auto-dismiss timer. */
const ToastItem = ({ toast, onClose }) => {
    const { t } = useTranslation();
    const [leaving, setLeaving] = useState(false);

    const dismiss = useCallback(() => {
        setLeaving(true);
        // Wait for the exit transition before removing from the list.
        setTimeout(() => onClose(toast.id), 200);
    }, [toast.id, onClose]);

    return (
        <div
            className={`sb-toast sb-toast--${toast.type}${leaving ? ' is-leaving' : ''}`}
            role={toast.type === 'error' ? 'alert' : 'status'}
            aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
        >
            <span className="sb-toast__icon">{ICONS[toast.type] || ICONS.default}</span>

            <div className="sb-toast__body">
                <div className="sb-toast__title">
                    {toast.title || (DEFAULT_TITLE[toast.type] || DEFAULT_TITLE.default)()}
                    {toast.count > 1 && <span className="sb-toast__count">×{toast.count}</span>}
                </div>
                <div className="sb-toast__msg">{toast.message}</div>
            </div>

            <button type="button" className="sb-toast__close" onClick={dismiss} aria-label={t('toaster.dismiss', 'Dismiss notification')}>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>

            {toast.duration != null && (
                <span
                    key={toast.nonce}                 /* remount to restart the timer when a duplicate lands */
                    className="sb-toast__bar"
                    style={{ animationDuration: `${toast.duration}ms` }}
                    onAnimationEnd={dismiss}
                />
            )}
        </div>
    );
};

const POSITIONS = Object.values(POSITION);

// Most requests on a page fail together when the server is unreachable, so keep
// the stack from spamming: collapse identical toasts into one (with a count) and
// never show more than this many at once.
const MAX_TOASTS = 4;
const dedupeKey = (t) => `${t.type}::${t.message}`;

const Toaster = () => {
    const [toasts, setToasts] = useState([]);

    useEffect(() => subscribe((action) => {
        if (action.kind === 'add') {
            setToasts((prev) => {
                // Already showing this exact message? Bump its count and restart its
                // timer (via nonce) instead of stacking a duplicate.
                const key = dedupeKey(action.toast);
                const dupe = prev.find((t) => dedupeKey(t) === key);
                if (dupe) {
                    return prev.map((t) =>
                        t === dupe
                            ? { ...t, count: (t.count || 1) + 1, nonce: (t.nonce || 0) + 1 }
                            : t
                    );
                }
                // Otherwise add it (replacing any toast that reused this id), capped
                // to the newest MAX_TOASTS.
                const rest = prev.filter((t) => t.id !== action.toast.id);
                return [...rest, { ...action.toast, count: 1, nonce: 0 }].slice(-MAX_TOASTS);
            });
        } else if (action.kind === 'remove') {
            setToasts((prev) => prev.filter((t) => t.id !== action.id));
        } else if (action.kind === 'clear') {
            setToasts([]);
        }
    }), []);

    const remove = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    if (toasts.length === 0) return null;

    return (
        <>
            {POSITIONS.map((pos) => {
                const group = toasts.filter((t) => t.position === pos);
                if (group.length === 0) return null;
                return (
                    <div key={pos} className={`sb-toaster sb-toaster--${pos}`} aria-live="polite">
                        {group.map((t) => (
                            <ToastItem key={t.id} toast={t} onClose={remove} />
                        ))}
                    </div>
                );
            })}
        </>
    );
};

export default Toaster;
